import { Autocomplete, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Drawer, FormControl, FormLabel, Grid, Input, ModalClose, Radio, RadioGroup, Textarea } from "@mui/joy";
import React from "react";
import { Elevator } from "../Interfaces/Elevator";
import { Inspection } from "../Pages/InspectionsList";
import { Building } from "../Pages/BuildingList";
import { Mechanic } from "../Interfaces/Mechanic";
import { Util } from "../Helpers/Util";
import { DatePicker } from "@mui/x-date-pickers";

interface AttemptPdf {
	inspection_attempt_pdf_id: string;
	attempt_number: number;
	file_name: string;
	blob_url: string;
}

interface InspectionDialogProps {
	insp: Inspection | undefined;
	close: Function;
	open: boolean;
	buildings: Array<Building>;
	elevators: Array<Elevator>;
	mechanics: Array<Mechanic>;
	companies: Array<string>;
	alteration: boolean;
	annual: boolean;
}

export default function InspectionDialog(props: InspectionDialogProps) {
	const [insp, setInsp] = React.useState<Inspection>({} as Inspection);
	const [open, setOpen] = React.useState(false);
	const [company, setCompany] = React.useState("");
	const [render, setRender] = React.useState(false);
	const [attemptPdfs, setAttemptPdfs] = React.useState<Array<AttemptPdf>>([]);

	React.useEffect(() => {
		setOpen(props.open);
		const insp = props.insp || ({} as Inspection);
		insp.Alteration = props.alteration;
		insp.Notes = insp.Notes ?? "";
		if (!insp.ID) {
			insp.FifthDate = null;
			insp.FirstDate = null;
			insp.SecondDate = null;
			insp.ThirdDate = null;
			insp.FourthDate = null;
			insp.SixthDate = null;
			insp.SeventhDate = null;
			insp.EighthDate = null;
			insp.NinthDate = null;
			insp.TenthDate = null;
			insp.Passed = undefined;
			insp.SecondPassed = undefined;
			insp.ThirdPassed = undefined;
			insp.FourthPassed = undefined;
			insp.FifthPassed = undefined;
			insp.SixthPassed = undefined;
			insp.SeventhPassed = undefined;
			insp.EighthPassed = undefined;
			insp.NinthPassed = undefined;
			insp.TenthPassed = undefined;
			setAttemptPdfs([]);
		} else if (insp.InspectionsID) {
			// Load existing attempt PDFs for this inspection record
			fetch("Inspection/GetInspectionAttemptPdfs", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(insp.InspectionsID),
			})
				.then((x) => x.json())
				.then((x) => setAttemptPdfs(x as Array<AttemptPdf>));
		}
		setCompany(insp.Company);
		setInsp(insp);
		setRender(true);
	}, [props.open, props.insp, props.alteration]);

	// Uploads a PDF for a specific attempt number (1-based)
	const uploadAttemptPdf = (attemptNumber: number, file: File) => {
		if (!insp.InspectionsID) {
			alert("Save the inspection record first before attaching a PDF.");
			return;
		}
		const fd = new FormData();
		fd.append("file", file);
		fd.append("inspectionsId", insp.InspectionsID);
		fd.append("attemptNumber", attemptNumber.toString());
		fetch("Inspection/SaveInspectionAttemptPdf", { method: "POST", body: fd }).then(() => {
			// Reload the attempt PDFs to reflect the newly uploaded file
			fetch("Inspection/GetInspectionAttemptPdfs", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(insp.InspectionsID),
			})
				.then((x) => x.json())
				.then((x) => setAttemptPdfs(x as Array<AttemptPdf>));
		});
	};

	const close = () => {
		props.close();
	};

	const sizes = {
		xs: 12,
		sm: 6,
		md: 4,
		lg: 3,
	};

	const threeSizes = {
		sm: 12,
		md: 6,
		lg: 4,
	};

	// Renders a compact PDF upload/view chip for a given attempt number
	const renderAttemptPdfUpload = (attemptNumber: number) => {
		const existing = attemptPdfs.find((p) => p.attempt_number === attemptNumber);
		const inputRef = React.createRef<HTMLInputElement>();
		return (
			<Grid xs={12} sx={{ display: "flex", alignItems: "center", gap: 1, mt: -1, mb: 1 }}>
				<input
					ref={inputRef}
					type="file"
					accept="application/pdf"
					style={{ display: "none" }}
					onChange={(e) => {
						if (e.target.files && e.target.files[0]) uploadAttemptPdf(attemptNumber, e.target.files[0]);
					}}
				/>
				<Button size="sm" variant="outlined" onClick={() => inputRef.current?.click()}>
					{existing ? "Replace PDF" : "Attach PDF"}
				</Button>
				{existing && (
					<Chip
						variant="soft"
						color="success"
						onClick={() => window.open(existing.blob_url, "_blank")}
						sx={{ cursor: "pointer" }}>
						{Util.stripPdfExtension(existing.file_name)}
					</Chip>
				)}
			</Grid>
		);
	};

	const getElevators = () => {
		const buildingId = insp?.BuildingID;
		let elevators = [] as Array<Elevator>;
		if (!Util.isNullOrEmpty(buildingId)) {
			return props.elevators.filter((x) => x.buildings_id.toLowerCase() === buildingId?.toLowerCase());
		}

		return elevators;
	};

	const deleteInsp = () => {
		if (!window.confirm("This cannot be undone, continue?")) return;

		fetch("Inspection/DeleteAnnualInspection", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify(insp.InspectionsID),
		}).then((x) => close());
	};

	const save = () => {
		if (Util.isNullOrEmpty(insp.ElevatorsID)) {
			alert("A building & elevator is required.");
			return;
		}
		fetch("Inspection/SaveInspections", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify([{ ...insp, Annual: props.annual }]),
		}).then((x) => close());
	};

	return !render ? <></> : (
		<>
			<Drawer anchor="right" open={open} size="lg">
				<ModalClose onClick={close} />
				<DialogTitle>Inspection</DialogTitle>
				<DialogContent sx={{ padding: 2 }}>
					<Grid container spacing={2} sx={{ overflowX: "hidden" }}>
						<Grid xs={12}>
							<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Inspection Information</FormLabel>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Building</FormLabel>
								<Autocomplete
									options={props.buildings}
									getOptionLabel={(opt: Building) => opt.Name}
									openOnFocus
									value={props.buildings.find((x) => x.ID === insp?.BuildingID) || null}
									onChange={(event, value) => {
										if (insp && value) {
											setInsp({ ...insp, BuildingName: value?.Name, BuildingID: value.ID });
										}
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Elevator</FormLabel>
								<Autocomplete
									options={getElevators()}
									getOptionLabel={(opt: Elevator) => opt.serial_number}
									openOnFocus
									value={props.elevators.find((x) => x.elevators_id === insp?.ElevatorsID) || null}
									onChange={(event, value) => {
										if (insp && value) {
											setInsp({ ...insp, ElevatorsID: value.elevators_id, SerialNumber: value.serial_number });
										}
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Mechanic</FormLabel>
								<Autocomplete
									options={props.mechanics}
									getOptionLabel={(opt: Mechanic) => opt.full_name}
									openOnFocus
									value={props.mechanics.find((x) => x.users_id === insp?.MechanicID) || null}
									onChange={(event, value) => {
										if (insp && value) {
											setInsp({ ...insp, MechanicID: value.users_id });
										}
									}}
								/>
							</FormControl>
						</Grid>
						<Grid {...threeSizes}>
							<FormControl>
								<FormLabel>Company</FormLabel>
								<Autocomplete
									options={props.companies}
									openOnFocus
									value={company}
									onChange={(event, value) => {
										if (insp && value) {
											if (value.startsWith("Add '")) {
												value = value.split("Add '")[1];
												value = value.substring(0, value.length - 1);
											}
											setInsp({ ...insp, Company: value });
											setCompany(value);
										}
									}}
									filterOptions={(options: string[], params: any) => {
										const res = options.filter((x) => x.toLowerCase().includes(params.inputValue.toLowerCase()));

										if (!Util.isNullOrEmpty(params.inputValue) && typeof options.find((x) => x.toLowerCase() === params.inputValue.toLowerCase()) === "undefined") {
											res.push("Add '" + params.inputValue + "'");
										}

										return res;
									}}
									freeSolo
								/>
							</FormControl>
						</Grid>
						<Grid {...sizes}>
							<FormControl>
								<FormLabel>Expiration Date</FormLabel>
								<DatePicker
									slotProps={{
										textField: { variant: "standard", size: "small" },
									}}
									value={insp?.ExpirationDate}
									format="MM/dd/yyyy"
									onChange={(value) => {
										setInsp({ ...insp, ExpirationDate: value as Date });
									}}
								/>
							</FormControl>
						</Grid>
						<Grid xs={12}>
							<FormControl>
								<FormLabel>Notes</FormLabel>
								<Textarea minRows={5} value={insp.Notes} onChange={(event) => setInsp({ ...insp, Notes: event.target.value })} />
							</FormControl>
						</Grid>
						<Grid xs={12}>
							<FormLabel sx={{ typography: "title-md", fontWeight: "bold" }}>Inspection Results</FormLabel>
						</Grid>
						<Grid {...sizes}>
							<FormControl>
								<FormLabel>First Inspection Passed?</FormLabel>
								<RadioGroup
									orientation="horizontal"
									defaultValue={insp?.Passed ? "yes" : insp?.Passed === false ? "no" : ""}
									onChange={(event) => {
										setInsp({ ...insp, Passed: event.target.value === "yes" });
									}}
									name="first-passed">
									<Radio value="yes" label="Yes" variant="outlined" />
									<Radio value="no" label="No" variant="outlined" />
								</RadioGroup>
							</FormControl>
						</Grid>
						<Grid {...sizes}>
							<FormControl>
								<FormLabel>First Inspection Date</FormLabel>
								<DatePicker
									slotProps={{
										textField: { variant: "standard", size: "small" },
									}}
									value={insp?.FirstDate}
									format="MM/dd/yyyy"
									onChange={(value) => {
										setInsp({ ...insp, FirstDate: value as Date });
									}}
								/>
							</FormControl>
						</Grid>
						{insp.FirstDate && renderAttemptPdfUpload(1)}
						<Grid visibility={insp.FirstDate && insp.Passed === false ? "visible" : "hidden"} container xs={12}>
							<Grid {...sizes}>
								<FormControl>
									<FormLabel>Second Inspection Passed?</FormLabel>
									<RadioGroup
										orientation="horizontal"
										defaultValue={insp?.SecondPassed ? "yes" : insp?.SecondPassed === false ? "no" : ""}
										onChange={(event) => {
											setInsp({ ...insp, SecondPassed: event.target.value === "yes" });
										}}
										name="second-passed">
										<Radio disabled={insp.Passed || !insp.FirstDate} value="yes" label="Yes" variant="outlined" />
										<Radio disabled={insp.Passed || !insp.FirstDate} value="no" label="No" variant="outlined" />
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid {...sizes}>
								<FormControl>
									<FormLabel>Second Inspection Date</FormLabel>
									<DatePicker
										slotProps={{ textField: { variant: "standard", size: "small" } }}
										value={insp?.SecondDate}
										format="MM/dd/yyyy"
										onChange={(value) => { setInsp({ ...insp, SecondDate: value as Date }); }}
									/>
								</FormControl>
							</Grid>
							{insp.SecondDate && renderAttemptPdfUpload(2)}
						</Grid>
						<Grid visibility={insp.SecondDate && insp.SecondPassed === false ? "visible" : "hidden"} container xs={12}>
							<Grid {...sizes}>
								<FormControl>
									<FormLabel>Third Inspection Passed?</FormLabel>
									<RadioGroup
										orientation="horizontal"
										defaultValue={insp?.ThirdPassed ? "yes" : insp?.ThirdPassed === false ? "no" : ""}
										onChange={(event) => { setInsp({ ...insp, ThirdPassed: event.target.value === "yes" }); }}
										name="third-passed">
										<Radio disabled={insp.SecondPassed || insp.Passed || !insp.SecondDate} value="yes" label="Yes" variant="outlined" />
										<Radio disabled={insp.SecondPassed || insp.Passed || !insp.SecondDate} value="no" label="No" variant="outlined" />
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid {...sizes}>
								<FormControl>
									<FormLabel>Third Inspection Date</FormLabel>
									<DatePicker
										slotProps={{ textField: { variant: "standard", size: "small" } }}
										value={insp?.ThirdDate}
										format="MM/dd/yyyy"
										onChange={(value) => { setInsp({ ...insp, ThirdDate: value as Date }); }}
									/>
								</FormControl>
							</Grid>
							{insp.ThirdDate && renderAttemptPdfUpload(3)}
						</Grid>
						<Grid visibility={insp.ThirdDate && insp.ThirdPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Fourth Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.FourthPassed ? "yes" : insp?.FourthPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, FourthPassed: event.target.value === "yes" });
                                    }}
                                    name="fourth-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Fourth Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.FourthDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, FourthDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.FourthDate && renderAttemptPdfUpload(4)}
                    </Grid>

                    <Grid visibility={insp.FourthDate && insp.FourthPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Fifth Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.FifthPassed ? "yes" : insp?.FifthPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, FifthPassed: event.target.value === "yes" });
                                    }}
                                    name="Fifth-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Fifth Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.FifthDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, FifthDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.FifthDate && renderAttemptPdfUpload(5)}
                    </Grid>

                    <Grid visibility={insp.FifthDate && insp.FifthPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Sixth Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.SixthPassed ? "yes" : insp?.SixthPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, SixthPassed: event.target.value === "yes" });
                                    }}
                                    name="Sixth-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Sixth Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.SixthDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, SixthDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.SixthDate && renderAttemptPdfUpload(6)}
                    </Grid>

                    <Grid visibility={insp.SixthDate && insp.SixthPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Seventh Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.SeventhPassed ? "yes" : insp?.SeventhPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, SeventhPassed: event.target.value === "yes" });
                                    }}
                                    name="Seventh-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Seventh Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.SeventhDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, SeventhDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.SeventhDate && renderAttemptPdfUpload(7)}
                    </Grid>
                    <Grid visibility={insp.SeventhDate && insp.SeventhPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Eigth Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.EighthPassed ? "yes" : insp?.EighthPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, EighthPassed: event.target.value === "yes" });
                                    }}
                                    name="Eighth-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Eigth Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.EighthDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, EighthDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.EighthDate && renderAttemptPdfUpload(8)}
                    </Grid>
                    <Grid visibility={insp.EighthDate && insp.EighthPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Ninth Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.NinthPassed ? "yes" : insp?.NinthPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, NinthPassed: event.target.value === "yes" });
                                    }}
                                    name="Ninth-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Ninth Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.NinthDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, NinthDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.NinthDate && renderAttemptPdfUpload(9)}
                    </Grid>
                    <Grid visibility={insp.NinthDate && insp.NinthPassed === false ? "visible" : "hidden"} container xs={12}>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Tenth Inspection Passed?</FormLabel>
                                <RadioGroup
                                    orientation="horizontal"
                                    defaultValue={insp?.TenthPassed ? "yes" : insp?.TenthPassed === false ? "no" : ""}
                                    onChange={(event) => {
                                        setInsp({ ...insp, TenthPassed: event.target.value === "yes" });
                                    }}
                                    name="Tenth-passed">
                                    <Radio value="yes" label="Yes" variant="outlined" />
                                    <Radio value="no" label="No" variant="outlined" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid {...sizes}>
                            <FormControl>
                                <FormLabel>Tenth Inspection Date</FormLabel>
                                <DatePicker
                                    slotProps={{
                                        textField: { variant: "standard", size: "small" },
                                    }}
                                    value={insp?.TenthDate}
                                    format="MM/dd/yyyy"
                                    onChange={(value) => {
                                        setInsp({ ...insp, TenthDate: value as Date });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        {insp.TenthDate && renderAttemptPdfUpload(10)}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Box sx={{ padding: 2 }}>
                    <Button hidden={props.insp?.InspectionsID ? false : true} size="lg" color="primary" onClick={deleteInsp} sx={{ mx: 1 }}>
                        Delete
                    </Button>
                    <Button size="lg" color="primary" onClick={close} sx={{ mx: 1 }}>
                        Close
                    </Button>
                    <Button size="lg" color="primary" onClick={save} sx={{ mx: 1 }}>
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Drawer>
    </>
	);
}
